import { useEffect, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Check, Copy, Users } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import { eventSettingsQueryOptions, patchEvent, type EventSettingsRow } from "@/core/settings"
import {
  GUEST_LIFECYCLE,
  actionErrorMessage,
  guestDisplayName,
  guestsQueryOptions,
  runGuestLifecycle,
  type Guest,
} from "@/core/people"
import { publicEventSiteUrl } from "@/core/event-site"
import { ApiError } from "@/core/api"
import {
  Card,
  Field,
  GhostButton,
  PrimaryButton,
  Select,
  SettingsPage,
  SettingsPageFooter,
  TextInput,
} from "@/components/settings/primitives"
import { DateTimePicker } from "@/components/ui/DateTimePicker"
import { cn } from "@/core/utils"
import { normalizeWallClockDateTime, wallClockDayKey } from "@/core/local-datetime"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/registration")({
  component: RegistrationView,
})

type Method = "invite_only" | "public_page" | "external"

const METHODS: { value: Method; label: string; blurb: string }[] = [
  { value: "invite_only", label: "Invite-only", blurb: "Guests join by email invitation." },
  { value: "public_page", label: "Public page", blurb: "Anyone with the link can register." },
  { value: "external", label: "External", blurb: "Registration handled on another platform." },
]

type FormState = {
  registrationMethod: Method
  publicRegistrationMode: "request" | "auto-confirm"
  maxCapacity: string
  rsvpDeadline: string
}

function pickerDateTime(value: string | null | undefined): string {
  if (!value) return ""
  try {
    return normalizeWallClockDateTime(value, false)
  } catch {
    return ""
  }
}

function RegistrationView() {
  const { eventId, orgSlug, eventSlug, row: eventRow } = useActiveEvent()
  const eventStart = wallClockDayKey(eventRow?.startsAt) ?? undefined
  const eventEnd = wallClockDayKey(eventRow?.endsAt) ?? undefined
  const qc = useQueryClient()
  const q = useQuery({ ...eventSettingsQueryOptions(eventId ?? ""), enabled: !!eventId })

  const [form, setForm] = useState<FormState | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!q.data || dirty) return
    setForm({
      registrationMethod: q.data.registrationMethod ?? "invite_only",
      publicRegistrationMode: q.data.publicRegistrationMode ?? "request",
      maxCapacity: q.data.maxCapacity != null ? String(q.data.maxCapacity) : "",
      rsvpDeadline: pickerDateTime(q.data.rsvpDeadline),
    })
  }, [q.data, dirty])

  const save = useMutation({
    mutationFn: () => {
      if (!eventId || !form) throw new Error("Not ready")
      const patch: Partial<EventSettingsRow> = {
        registrationMethod: form.registrationMethod,
        publicRegistrationEnabled: form.registrationMethod === "public_page" ? 1 : 0,
        publicRegistrationMode: form.publicRegistrationMode,
        maxCapacity: form.maxCapacity.trim() === "" ? null : Number(form.maxCapacity),
        rsvpDeadline: form.rsvpDeadline.trim() === ""
          ? null
          : normalizeWallClockDateTime(form.rsvpDeadline),
      }
      return patchEvent(eventId, patch)
    },
    onSuccess: () => {
      setDirty(false)
      toast.success("Registration settings saved")
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Failed to save registration"),
  })

  const update = (patch: Partial<FormState>) => {
    setDirty(true)
    setForm((f) => (f ? { ...f, ...patch } : f))
  }

  return (
    <SettingsPage
      title="Registration"
      description="How guests get onto the list, and the limits that gate it."
    >
      {q.isError && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {(q.error as Error)?.message ?? "Failed to load settings"}
          </p>
        </Card>
      )}

      <Card title="Registration method">
        <div className="grid gap-2.5 @[560px]:grid-cols-3">
          {METHODS.map((m) => {
            const active = form?.registrationMethod === m.value
            return (
              <button
                key={m.value}
                type="button"
                disabled={!form}
                onClick={() => update({ registrationMethod: m.value })}
                className={cn(
                  "rounded-xl border px-3.5 py-3 text-left transition disabled:opacity-50",
                  active
                    ? "border-[var(--accent)] bg-[var(--accent)]/8 ring-1 ring-[var(--accent)]/30"
                    : "border-plum-200/60 hover:border-plum-300/70 dark:border-white/10 dark:hover:border-white/20",
                )}
              >
                <span className="block text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                  {m.label}
                </span>
                <span className="mt-0.5 block text-[12px] text-plum-700/55 dark:text-plum-200/40">
                  {m.blurb}
                </span>
              </button>
            )
          })}
        </div>

        {form?.registrationMethod === "public_page" && (
          <Field
            label="When someone registers"
            hint="Hold for approval, or auto-confirm seats until capacity is reached."
          >
            <Select
              value={form.publicRegistrationMode}
              onChange={(e) =>
                update({ publicRegistrationMode: e.target.value as "request" | "auto-confirm" })
              }
            >
              <option value="request">Hold for approval</option>
              <option value="auto-confirm">Auto-confirm until full</option>
            </Select>
          </Field>
        )}

        {form?.registrationMethod === "public_page" && orgSlug && eventSlug && (
          <Field
            label="Registration link"
            hint="The public event site. Anyone with this link can register."
          >
            <RegistrationLink url={publicEventSiteUrl(orgSlug, eventSlug)} />
          </Field>
        )}
      </Card>

      <Card title="Limits">
        <Field label="Max capacity" hint="Leave blank for no cap.">
          <TextInput
            type="number"
            min={0}
            inputMode="numeric"
            placeholder="Unlimited"
            value={form?.maxCapacity ?? ""}
            disabled={!form}
            onChange={(e) => update({ maxCapacity: e.target.value })}
          />
        </Field>
        <Field
          label="RSVP deadline"
          hint="Event-local date & time. Leave blank for no deadline."
        >
          <DateTimePicker
            mode="datetime"
            value={form?.rsvpDeadline ?? ""}
            disabled={!form}
            onChange={(v) => update({ rsvpDeadline: v })}
            eventStart={eventStart}
            eventEnd={eventEnd}
            placeholder="No deadline"
            ariaLabel="RSVP deadline"
          />
        </Field>
      </Card>

      <PendingApprovalQueue eventId={eventId ?? null} />

      <SettingsPageFooter>
        <PrimaryButton onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save changes"}
        </PrimaryButton>
      </SettingsPageFooter>
    </SettingsPage>
  )
}

// ── public registration link ──────────────────────────────────────────────────

function RegistrationLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center gap-2 rounded-lg border border-plum-200/70 bg-plum-50/50 px-3 py-2 dark:border-white/10 dark:bg-white/[0.03]">
      <span className="min-w-0 flex-1 truncate font-mono text-[12.5px] text-plum-800 dark:text-plum-100">
        {url}
      </span>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(url)
          setCopied(true)
          window.setTimeout(() => setCopied(false), 1500)
        }}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-plum-700/60 transition hover:bg-plum-100/70 hover:text-plum-900 dark:text-plum-200/50 dark:hover:bg-white/10 dark:hover:text-plum-50"
        aria-label="Copy registration link"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  )
}

// ── pending-approval queue ─────────────────────────────────────────────────────
// Reuses the event guests query (filtered to status="pending" client-side) and
// the shared guest-lifecycle runner for Approve / Decline.

function PendingApprovalQueue({ eventId }: { eventId: string | null }) {
  const qc = useQueryClient()
  const q = useQuery({ ...guestsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const [busyId, setBusyId] = useState<string | null>(null)

  const pending = (q.data ?? []).filter((g) => g.status === "pending")

  async function run(
    guest: Guest,
    kind: "approve" | "approve-invite" | "cancel-invite",
  ) {
    setBusyId(guest.id)
    const isApprove = kind === "approve" || kind === "approve-invite"
    // "approve-invite" reuses the approve action with sendInvite:true so the
    // guest is confirmed AND emailed their credential in one step.
    const spec = isApprove ? GUEST_LIFECYCLE.approve : GUEST_LIFECYCLE["cancel-invite"]
    const body = kind === "approve-invite" ? { sendInvite: true } : {}
    try {
      await runGuestLifecycle(qc, guest, spec, body)
      toast.success(
        kind === "approve-invite" ? "Approved & invited" : isApprove ? "Approved" : "Declined",
      )
    } catch (err) {
      toast.error(actionErrorMessage(err, isApprove ? "Approve failed" : "Decline failed"))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Card
      title="Pending approval"
      description="Public registrants waiting on a decision."
    >
      {q.isLoading ? (
        <div className="divide-y divide-plum-200/50 dark:divide-white/10">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0 space-y-1.5">
                <div className="h-3.5 w-32 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                <div className="h-3 w-44 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <div className="h-8 w-20 animate-pulse rounded-lg bg-plum-100 dark:bg-white/10" />
                <div className="h-8 w-20 animate-pulse rounded-lg bg-plum-100 dark:bg-white/10" />
              </div>
            </div>
          ))}
        </div>
      ) : pending.length === 0 ? (
        <div className="flex items-center gap-2 py-2 text-[13px] text-plum-700/55 dark:text-plum-200/40">
          <Users className="h-4 w-4" />
          No one is waiting on approval right now.
        </div>
      ) : (
        <div className="divide-y divide-plum-200/50 dark:divide-white/10">
          {pending.map((g) => {
            const company = g.companyAtInvite?.trim()
            const note = g.responseNote?.trim()
            return (
              <div
                key={g.id}
                className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                    {guestDisplayName(g)}
                  </div>
                  <div className="truncate text-[12px] text-plum-700/55 dark:text-plum-200/40">
                    {[g.emailAtInvite, company].filter(Boolean).join(" · ") || "—"}
                  </div>
                  {note && (
                    <div className="mt-1 text-[12px] italic text-plum-700/55 dark:text-plum-200/40">
                      “{note}”
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <PrimaryButton
                    className="h-8 px-3 text-[12px]"
                    disabled={busyId === g.id}
                    onClick={() => run(g, "approve-invite")}
                  >
                    Approve &amp; invite
                  </PrimaryButton>
                  <GhostButton
                    className="h-8 px-3 text-[12px]"
                    disabled={busyId === g.id}
                    onClick={() => run(g, "approve")}
                    title="Confirm their seat without sending an invite email"
                  >
                    Approve only
                  </GhostButton>
                  <GhostButton
                    className="h-8 px-3 text-[12px]"
                    disabled={busyId === g.id}
                    onClick={() => run(g, "cancel-invite")}
                  >
                    Decline
                  </GhostButton>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}
